import { useEffect, useState, Dispatch, useRef } from 'react';
import DynamicWidthNumberInput from '../DynamicWidthNumberInput';
import { IGlobalState, GlobalAction, GlobalActionTypeList } from '../../states/GlobalState';
import './index.scss';
import { Close as CloseIcon, ZoomIn, ZoomOut, FileCopy, AssignmentReturned, Delete, SaveAlt, Publish } from '@material-ui/icons';
import CONST from '../../consts';
import { RealtimeClient } from '../../services/realtime-client';
import { UbiverseEventTopicList } from '../../types';
import { getPlayheadDataFromTime, getSecondsPerBeat } from '../../states/utils';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
} from '@ubiquitel/ui';

import TimelineNote from '../TimelineNote';
import TimeIndicator from '../TimeIndicator';

export interface EditorPanelProp {
  state: IGlobalState;
  dispatch: Dispatch<GlobalAction>;
  realtimeClient: RealtimeClient;
}

const MIN_TIMELINE_ZOOM = 0.1
const BASE_COLUMN_COUNT = 25
const TEMP_COLUMN_COUNT = Math.ceil(BASE_COLUMN_COUNT / MIN_TIMELINE_ZOOM)
const ROW_COUNT = CONST.sampleDelay.numberOfIntensities
const ERASER_EDGE_THRESHOLD_PX = 6
const NOTE_ERASER_EDGE_THRESHOLD_PX = 8
const PLAYHEAD_DRAG_EDGE_PX = 24
const PLAYHEAD_DRAG_SCROLL_PX = 12
const PLAYHEAD_TRACK_HEIGHT_PX = 32
const DEFAULT_EXPORT_FILE_NAME = 'timeline-data'

type TimelineExportPayload = {
  version: 1
  exportedAt: string
  control: {
    bpm: number
    signature: {
      numerator: number
      denominator: number
    }
  }
  sequence: {
    id: string
    isMuted: boolean
    notes: { position: number; intensity: number }[]
  }
}

const EditorPanel = ({ state, dispatch, realtimeClient }: EditorPanelProp) => {  
  const timelineRef = useRef<HTMLDivElement>(null)
  const pianoRollRef = useRef<HTMLDivElement>(null)
  const [cellSize, setCellSize] = useState({ width: 0, height: 0 })
  const [draggingSequenceId, setDraggingSequenceId] = useState<string | null>(null)
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false)
  const [tempColumnCount, setTempColumnCount] = useState(TEMP_COLUMN_COUNT)
  const [selectionRange, setSelectionRange] = useState<{ startBeat: number; endBeat: number } | null>(null)
  const [copiedPattern, setCopiedPattern] = useState<{ notes: { position: number; intensity: number }[]; length: number } | null>(null)
  const [pendingImportPayload, setPendingImportPayload] = useState<TimelineExportPayload | null>(null)
  const [rememberImportChoice, setRememberImportChoice] = useState(false)
  const selectionStartRef = useRef<number | null>(null)
  const isSelectingRef = useRef(false)
  const selectionDragOccurredRef = useRef(false)
  // const [isEraserHover, setIsEraserHover] = useState(false)
  // const [eraserPosition, setEraserPosition] = useState<{ x: number, y: number } | null>(null)
  // const [eraserSource, setEraserSource] = useState<'piano' | 'note' | null>(null)
  const [noteEraserTargetId, setNoteEraserTargetId] = useState<string | null>(null)
  const importInputRef = useRef<HTMLInputElement>(null)
  const activeSequence = state.sequences.find(s => s.id === state.editor.targetId)
  const activeSequenceName = activeSequence?.name || ''
  const isMuted = activeSequence?.isMuted ?? false
  const hasSelection = selectionRange && Math.abs(selectionRange.endBeat - selectionRange.startBeat) > 0.01
  const storedImportChoice = typeof window !== 'undefined'
    ? window.localStorage.getItem('timelineImportChoice')
    : null

  useEffect(() => {
    console.log('noteEraserTargetId', noteEraserTargetId)
  }, [noteEraserTargetId])

  useEffect(() => {
    if (!pianoRollRef.current) return
    const updateCellSize = () => {
      if (!pianoRollRef.current || !pianoRollRef.current.children.length) return
      const firstCell = pianoRollRef.current.children[0] as HTMLDivElement
      const { width, height } = firstCell.getBoundingClientRect()
      if (!width || !height) return
      if (cellSize.width !== width || cellSize.height !== height) {
        setCellSize({ width, height })
      }
    }
    const rafId = requestAnimationFrame(updateCellSize)
    const handleResize = () => updateCellSize()
    window.addEventListener('resize', handleResize)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', handleResize)
    }
  }, [state.editor.timeline.zoom, tempColumnCount, cellSize.height, cellSize.width])

  useEffect(() => {
    if (!timelineRef.current) return
  }, [state.control.playheadTime])
  
  const getRelativeCoordinates = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pianoRollRef.current) return null
    const containerRect = pianoRollRef.current.getBoundingClientRect()
    return { x: e.clientX - containerRect.left, y: e.clientY - containerRect.top }
  }

  const isNearBarRoot = (relativeX: number) => {
    if (cellSize.width <= 0) return false
    const beatIndex = Math.floor(relativeX / cellSize.width)
    const offsetInBeat = relativeX - beatIndex * cellSize.width
    const isBarStart = beatIndex % state.control.signature.numerator === 0
    return isBarStart && offsetInBeat <= ERASER_EDGE_THRESHOLD_PX
  }

  const isEraserMode = (e: React.MouseEvent<HTMLDivElement>, relativeX: number) => {
    return e.metaKey || e.ctrlKey || isNearBarRoot(relativeX)
  }

  const updatePlayheadFromRelativeX = (relativeX: number) => {
    if (cellSize.width <= 0) return
    const rawBeat = relativeX / cellSize.width
    const quantizationPitch = (state.editor.quantization.numerator / state.editor.quantization.denominator) * 4
    const snappedBeat = state.editor.quantization.isActive
      ? Math.round(rawBeat / quantizationPitch) * quantizationPitch
      : rawBeat
    const playheadTime = Math.max(0, snappedBeat * getSecondsPerBeat(state.control.bpm, state.control.signature))
    const playheadData = getPlayheadDataFromTime(playheadTime, state.control.bpm, state.control.signature)
    dispatch({ type: GlobalActionTypeList.SetControlPlayheadTime, payload: playheadData.playheadTime })
    dispatch({ type: GlobalActionTypeList.SetControlPlayheadPosition, payload: { bar: playheadData.bar, beat: playheadData.beat } })
  }

  const getRelativeXFromTimeline = (clientX: number) => {
    if (!timelineRef.current || !pianoRollRef.current) return null
    const pianoRollRect = pianoRollRef.current.getBoundingClientRect()
    return clientX - pianoRollRect.left
  }

  const maybeAutoScrollTimeline = (clientX: number) => {
    if (!timelineRef.current) return
    if (cellSize.width <= 0) return
    const timelineRect = timelineRef.current.getBoundingClientRect()
    const quantizationPitch = (state.editor.quantization.numerator / state.editor.quantization.denominator) * 4
    const quantizationWidth = cellSize.width * (state.editor.quantization.isActive ? quantizationPitch : 1)
    const edgePadding = Math.max(PLAYHEAD_DRAG_EDGE_PX, quantizationWidth / 2)
    const leftEdge = timelineRect.left + edgePadding
    const rightEdge = timelineRect.right - edgePadding

    if (clientX <= leftEdge) {
      timelineRef.current.scrollLeft = Math.max(0, timelineRef.current.scrollLeft - PLAYHEAD_DRAG_SCROLL_PX)
    } else if (clientX >= rightEdge) {
      timelineRef.current.scrollLeft = timelineRef.current.scrollLeft + PLAYHEAD_DRAG_SCROLL_PX
    }
  }

  const handlePianoRollClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pianoRollRef.current || draggingSequenceId || isDraggingPlayhead) return
    if (selectionDragOccurredRef.current) {
      selectionDragOccurredRef.current = false
      return
    }
    if (pianoRollRef.current.contains(e.target as Node)) {
      const relativeCoordinates = getRelativeCoordinates(e)
      if (!relativeCoordinates) return
      const containerRect = pianoRollRef.current.getBoundingClientRect()

      const id = `note__${new Date().getTime()}`
      const intensity = Math.round( ROW_COUNT * (1.0 - (relativeCoordinates.y / containerRect.height)) )
      const quantizationPitch = (state.editor.quantization.numerator / state.editor.quantization.denominator) * 4
      const position = state.editor.quantization.isActive
        ? Math.round((relativeCoordinates.x / cellSize.width) / quantizationPitch) * quantizationPitch
        : relativeCoordinates.x / cellSize.width


      const existingSequence = state.sequences.find(s => s.id === state.editor.targetId)?.notes.find((s) => Math.abs(s.position - position) < 0.01)

      const shouldErase = isEraserMode(e, relativeCoordinates.x)

      if (existingSequence) {
        if (shouldErase) {
          dispatch({ type: GlobalActionTypeList.RemoveSequenceNote, payload: { sequenceId: state.editor.targetId, noteId: existingSequence.id } })
          return
        }
        if (intensity > 0) {
          dispatch({ type: GlobalActionTypeList.UpdateSequenceNoteById, payload: { sequenceId: state.editor.targetId, note: { id: existingSequence.id, intensity, position } } })
        } else {
          dispatch({ type: GlobalActionTypeList.RemoveSequenceNote, payload: { sequenceId: state.editor.targetId, noteId: existingSequence.id } })
        }
      } else {
        if (shouldErase) return
        if (intensity > 0) {
          dispatch({ type: GlobalActionTypeList.AddSequenceNote, payload: { sequenceId: state.editor.targetId, note: { id, intensity, position } } })
        }
      }
    }
  }

  const handlePianoRollDragStart = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
    e.preventDefault()
    e.stopPropagation()
    const noteRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
    const offsetY = e.clientY - noteRect.top
    const isNearBottom = noteRect.height - offsetY <= NOTE_ERASER_EDGE_THRESHOLD_PX
    if (noteEraserTargetId === id || isNearBottom) {
      dispatch({ type: GlobalActionTypeList.RemoveSequenceNote, payload: { sequenceId: state.editor.targetId, noteId: id } })
      setNoteEraserTargetId(null)
      // setEraserSource(null)
      // setIsEraserHover(false)
      // setEraserPosition(null)
      return
    }
    const relativeCoordinates = getRelativeCoordinates(e)
    if (relativeCoordinates && isEraserMode(e, relativeCoordinates.x)) {
      dispatch({ type: GlobalActionTypeList.RemoveSequenceNote, payload: { sequenceId: state.editor.targetId, noteId: id } })
      return
    }
    setDraggingSequenceId(id)
  }

  const getNormalizedSelection = (range: { startBeat: number; endBeat: number }) => {
    const startBeat = Math.min(range.startBeat, range.endBeat)
    const endBeat = Math.max(range.startBeat, range.endBeat)
    return { startBeat, endBeat }
  }

  const getSnappedBeat = (beat: number) => {
    if (!state.editor.quantization.isActive) return beat
    const quantizationPitch = (state.editor.quantization.numerator / state.editor.quantization.denominator) * 4
    return Math.round(beat / quantizationPitch) * quantizationPitch
  }

  const getPlayheadBeatPosition = () => {
    const playheadData = getPlayheadDataFromTime(state.control.playheadTime, state.control.bpm, state.control.signature)
    return (playheadData.bar - 1) * state.control.signature.numerator + (playheadData.beat - 1)
  }

  const movePlayheadToBeat = (beatPosition: number) => {
    const playheadTime = Math.max(0, beatPosition * getSecondsPerBeat(state.control.bpm, state.control.signature))
    const playheadData = getPlayheadDataFromTime(playheadTime, state.control.bpm, state.control.signature)
    dispatch({ type: GlobalActionTypeList.SetControlPlayheadTime, payload: playheadData.playheadTime })
    dispatch({ type: GlobalActionTypeList.SetControlPlayheadPosition, payload: { bar: playheadData.bar, beat: playheadData.beat } })
  }

  const handleCopySelection = () => {
    if (!activeSequence || !hasSelection) return
    const { startBeat, endBeat } = getNormalizedSelection(selectionRange as { startBeat: number; endBeat: number })
    if (endBeat - startBeat <= 0) return
    const selectedNotes = activeSequence.notes
      .filter(note => note.position >= startBeat && note.position <= endBeat)
      .map(note => ({ position: note.position - startBeat, intensity: note.intensity }))
    if (!selectedNotes.length) return
    setCopiedPattern({ notes: selectedNotes, length: endBeat - startBeat })
  }

  const handleDeleteSelection = () => {
    if (!activeSequence || !hasSelection) return
    const { startBeat, endBeat } = getNormalizedSelection(selectionRange as { startBeat: number; endBeat: number })
    if (endBeat - startBeat <= 0) return
    activeSequence.notes
      .filter(note => note.position >= startBeat && note.position <= endBeat)
      .forEach(note => {
        dispatch({ type: GlobalActionTypeList.RemoveSequenceNote, payload: { sequenceId: state.editor.targetId, noteId: note.id } })
      })
  }

  const isValidTimelinePayload = (payload: any): payload is TimelineExportPayload => {
    if (!payload || typeof payload !== 'object') return false
    if (payload.version !== 1) return false
    if (!payload.control || typeof payload.control !== 'object') return false
    if (typeof payload.control.bpm !== 'number') return false
    if (!payload.control.signature || typeof payload.control.signature !== 'object') return false
    if (typeof payload.control.signature.numerator !== 'number') return false
    if (typeof payload.control.signature.denominator !== 'number') return false
    if (!payload.sequence || typeof payload.sequence !== 'object') return false
    if (typeof payload.sequence.id !== 'string') return false
    if (typeof payload.sequence.isMuted !== 'boolean') return false
    if (!Array.isArray(payload.sequence.notes)) return false
    if (!payload.sequence.notes.every((note: any) => typeof note.position === 'number' && typeof note.intensity === 'number')) {
      return false
    }
    return true
  }

  const buildExportPayload = (): TimelineExportPayload | null => {
    if (!activeSequence) return null
    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      control: {
        bpm: state.control.bpm,
        signature: {
          numerator: state.control.signature.numerator,
          denominator: state.control.signature.denominator
        }
      },
      sequence: {
        id: activeSequence.id,
        isMuted: activeSequence.isMuted,
        notes: activeSequence.notes.map(note => ({
          position: note.position,
          intensity: note.intensity
        }))
      }
    }
  }

  const handleExportTimeline = () => {
    if (!activeSequence) return
    const exportPayload = buildExportPayload()
    if (!exportPayload) return
    const requestedName = window.prompt('Enter a file name for the timeline JSON.', DEFAULT_EXPORT_FILE_NAME)
    if (!requestedName) return
    const trimmedName = requestedName.trim()
    if (!trimmedName) return
    const fileName = trimmedName.endsWith('.json') ? trimmedName : `${trimmedName}.json`
    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleImportTimeline = () => {
    if (!activeSequence) return
    importInputRef.current?.click()
  }

  const convertImportedPosition = (
    position: number,
    importBpm: number,
    importSignature: { numerator: number; denominator: number },
    targetBpm: number,
    targetSignature: { numerator: number; denominator: number }
  ) => {
    const importSecondsPerBeat = getSecondsPerBeat(importBpm, importSignature)
    const targetSecondsPerBeat = getSecondsPerBeat(targetBpm, targetSignature)
    const timeInSeconds = position * importSecondsPerBeat
    return timeInSeconds / targetSecondsPerBeat
  }

  const applyImportedSequence = (payload: TimelineExportPayload, keepBars: boolean) => {
    if (!activeSequence) return
    const nextNotes = payload.sequence.notes.map((note, index) => ({
      id: `note__${new Date().getTime()}_${index}`,
      intensity: note.intensity,
      position: keepBars
        ? note.position
        : convertImportedPosition(
            note.position,
            payload.control.bpm,
            payload.control.signature,
            state.control.bpm,
            state.control.signature
          )
    }))
    dispatch({
      type: GlobalActionTypeList.UpdateSequenceById,
      payload: {
        ...activeSequence,
        isMuted: payload.sequence.isMuted,
        notes: nextNotes
      }
    })
  }

  const finalizeImportChoice = (payload: TimelineExportPayload, keepBars: boolean, shouldRemember: boolean) => {
    if (shouldRemember) {
      window.localStorage.setItem('timelineImportChoice', keepBars ? 'beats' : 'timestamps')
    } else {
      window.localStorage.removeItem('timelineImportChoice')
    }
    applyImportedSequence(payload, keepBars)
    setPendingImportPayload(null)
    setRememberImportChoice(false)
  }

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeSequence) return
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      if (!isValidTimelinePayload(parsed)) {
        window.alert('Invalid timeline file format.')
        return
      }
      const sameBpm = parsed.control.bpm === state.control.bpm
      const sameSignature = parsed.control.signature.numerator === state.control.signature.numerator
        && parsed.control.signature.denominator === state.control.signature.denominator
      if (!sameBpm || !sameSignature) {
        if (storedImportChoice === 'beats' || storedImportChoice === 'timestamps') {
          const shouldKeepBars = storedImportChoice === 'beats'
          if (shouldKeepBars) {
            dispatch({ type: GlobalActionTypeList.SetControlBpm, payload: parsed.control.bpm })
            dispatch({ type: GlobalActionTypeList.SetControlSignatureNumerator, payload: parsed.control.signature.numerator })
            dispatch({ type: GlobalActionTypeList.SetControlSignatureDenominator, payload: parsed.control.signature.denominator })
          }
          applyImportedSequence(parsed, shouldKeepBars)
          return
        }
        setPendingImportPayload(parsed)
        setRememberImportChoice(false)
        return
      }
      applyImportedSequence(parsed, true)
    } catch (error) {
      console.error('Failed to import timeline data:', error)
      window.alert('Failed to import timeline data. Please check the file content.')
    }
  }

  const handlePasteSelection = () => {
    if (!activeSequence || !copiedPattern || !copiedPattern.notes.length) return
    const pasteStart = getPlayheadBeatPosition()
    copiedPattern.notes.forEach((note, index) => {
      const id = `note__${new Date().getTime()}_${index}`
      dispatch({
        type: GlobalActionTypeList.AddSequenceNote,
        payload: {
          sequenceId: state.editor.targetId,
          note: {
            id,
            intensity: note.intensity,
            position: pasteStart + note.position
          }
        }
      })
    })
    movePlayheadToBeat(pasteStart + copiedPattern.length)
  }

  const handleSelectionStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pianoRollRef.current || cellSize.width <= 0) return
    if (draggingSequenceId || isDraggingPlayhead) return
    const target = e.target as HTMLElement
    if (target.closest('.component--timeline-note')) return
    const relativeCoordinates = getRelativeCoordinates(e)
    if (!relativeCoordinates) return
    const startBeat = Math.max(0, getSnappedBeat(relativeCoordinates.x / cellSize.width))
    selectionStartRef.current = startBeat
    isSelectingRef.current = true
    selectionDragOccurredRef.current = false
    setSelectionRange({ startBeat, endBeat: startBeat })
  }

  const handleSelectionMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelectingRef.current || !selectionStartRef.current || cellSize.width <= 0) return
    const relativeCoordinates = getRelativeCoordinates(e)
    if (!relativeCoordinates) return
    const endBeat = Math.max(0, getSnappedBeat(relativeCoordinates.x / cellSize.width))
    if (Math.abs(endBeat - selectionStartRef.current) > 0.05) {
      selectionDragOccurredRef.current = true
    }
    setSelectionRange({ startBeat: selectionStartRef.current, endBeat })
  }

  const handleSelectionEnd = () => {
    if (!isSelectingRef.current || !selectionStartRef.current) return
    isSelectingRef.current = false
    const startBeat = selectionStartRef.current
    selectionStartRef.current = null
    setSelectionRange((prev) => {
      if (!prev) return null
      const { startBeat: normalizedStart, endBeat: normalizedEnd } = getNormalizedSelection(prev)
      if (Math.abs(normalizedEnd - normalizedStart) <= 0.05) {
        return null
      }
      return { startBeat, endBeat: prev.endBeat }
    })
  }

  const handlePianoRollDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    const relativeCoordinates = getRelativeCoordinates(e)
    if (!relativeCoordinates || !pianoRollRef.current) return
    const containerRect = pianoRollRef.current.getBoundingClientRect()
    // const shouldErase = isEraserMode(e, relativeCoordinates.x)
    // setIsEraserHover(shouldErase)
    if (!draggingSequenceId) return

    const quantizationPitch = (state.editor.quantization.numerator / state.editor.quantization.denominator) * 4
    const intensity = Math.round( ROW_COUNT * (1.0 - (relativeCoordinates.y / containerRect.height)) ) > 0 ? Math.round( ROW_COUNT * (1.0 - (relativeCoordinates.y / containerRect.height)) ) : 1
    const position = state.editor.quantization.isActive
      ? Math.round((relativeCoordinates.x / cellSize.width) / quantizationPitch) * quantizationPitch
      : relativeCoordinates.x / cellSize.width
    dispatch({ type: GlobalActionTypeList.UpdateSequenceNoteById, payload: { sequenceId: state.editor.targetId, note: { id: draggingSequenceId, intensity, position } } })
  }

  const handlePianoRollDragEnd = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setTimeout(() => { setDraggingSequenceId(null) }, 10)
  }

  const handlePianoRollMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const relativeCoordinates = getRelativeCoordinates(e)
    if (!relativeCoordinates) return
    if (isDraggingPlayhead) {
      handlePlayheadDragMove(e.clientX)
      return
    }
    if (isSelectingRef.current) {
      handleSelectionMove(e)
    }
    // const shouldErase = isEraserMode(e, relativeCoordinates.x)
    // setIsEraserHover(shouldErase)
    // setEraserPosition(shouldErase ? relativeCoordinates : null)
    // setEraserSource(shouldErase ? 'piano' : null)
    if (draggingSequenceId) {
      handlePianoRollDrag(e)
    }
  }

  const handlePlayheadDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const relativeX = getRelativeXFromTimeline(e.clientX)
    if (relativeX === null) return
    setIsDraggingPlayhead(true)
    updatePlayheadFromRelativeX(relativeX)
  }

  const handleTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (pianoRollRef.current?.contains(e.target as Node)) return
    handlePlayheadDragStart(e)
  }

  const handlePlayheadDragEnd = () => {
    setIsDraggingPlayhead(false)
  }

  const handlePlayheadDragMove = (clientX: number) => {
    const relativeX = getRelativeXFromTimeline(clientX)
    if (relativeX === null) return
    maybeAutoScrollTimeline(clientX)
    updatePlayheadFromRelativeX(relativeX)
  }

  useEffect(() => {
    if (!isDraggingPlayhead) return
    const handleMouseMove = (event: MouseEvent) => {
      handlePlayheadDragMove(event.clientX)
    }
    const handleMouseUp = () => {
      handlePlayheadDragEnd()
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingPlayhead])

  const handleNoteMouseMove = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
    if (!pianoRollRef.current) return
    const noteRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
    const offsetY = e.clientY - noteRect.top
    const isNearBottom = noteRect.height - offsetY <= NOTE_ERASER_EDGE_THRESHOLD_PX
    if (!isNearBottom) {
      if (noteEraserTargetId === id) {
        setNoteEraserTargetId(null)
        // setEraserSource(null)
        // setIsEraserHover(false)
        // setEraserPosition(null)
      }
      return
    }
    // const pianoRect = pianoRollRef.current.getBoundingClientRect()
    // setEraserPosition({
    //   x: noteRect.left + noteRect.width / 2 - pianoRect.left,
    //   y: noteRect.bottom - pianoRect.top
    // })
    // setIsEraserHover(true)
    // setEraserSource('note')
    setNoteEraserTargetId(id)
  }

  const handleNoteMouseEnter = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
    handleNoteMouseMove(e, id)
  }

  const handleNoteMouseLeave = (id: string) => {
    if (noteEraserTargetId !== id) return
    setNoteEraserTargetId(null)
    // setEraserSource(null)
    // setIsEraserHover(false)
    // setEraserPosition(null)
  }

  const getNextZoom = (direction: 'in' | 'out') => {
    const step = 0.1
    const delta = direction === 'in' ? step : -step
    return Math.max(MIN_TIMELINE_ZOOM, state.editor.timeline.zoom + delta)
  }

  const handleMuteToggle = () => {
    if (!activeSequence || state.control.isPlaying) return
    const nextIsMuted = !isMuted
    dispatch({ type: GlobalActionTypeList.UpdateSequenceById, payload: { ...activeSequence, isMuted: nextIsMuted } })
    realtimeClient.emitEvent(UbiverseEventTopicList.SetMute, { isMute: nextIsMuted, nodeId: activeSequence.id })
  }

  const handleTimelineWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey) return
    e.preventDefault()
    const direction = e.deltaY > 0 ? 'out' : 'in'
    dispatch({ type: GlobalActionTypeList.SetEditorTimelineZoom, payload: getNextZoom(direction) })
  }

  useEffect(() => {
    const handleGlobalWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return
      event.preventDefault()
    }
    window.addEventListener('wheel', handleGlobalWheel, { passive: false })
    return () => {
      window.removeEventListener('wheel', handleGlobalWheel)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!state.editor.targetId) return
      const target = event.target as HTMLElement | null
      const isEditableTarget = !!target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )
      if (isEditableTarget) return

      const isCopy = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c'
      const isPaste = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v'
      const isDelete = event.key === 'Delete' || event.key === 'Backspace'
      if (isCopy) {
        event.preventDefault()
        handleCopySelection()
      }
      if (isPaste) {
        event.preventDefault()
        handlePasteSelection()
      }
      if (isDelete) {
        event.preventDefault()
        handleDeleteSelection()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    state.editor.targetId,
    selectionRange,
    copiedPattern,
    activeSequence,
    state.control.playheadTime,
    state.control.bpm,
    state.control.signature,
    state.editor.quantization
  ])

  return (
    <div className="component--editor-panel"
      onClick={() => {
        dispatch({ type: GlobalActionTypeList.SetEditorTargetId, payload: '' })
      }}
    >
      <div className="component--editor-panel__container"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Editor Panel Header */}
        <div className="component--editor-panel__header">
          <input
            className="component--editor-panel__header__title-input"
            type="text"
            value={activeSequenceName}
            placeholder="Timeline name"
            onClick={(event) => {
              event.stopPropagation()
            }}
            onChange={(event) => {
              if (!activeSequence) return
              dispatch({
                type: GlobalActionTypeList.UpdateSequenceById,
                payload: { ...activeSequence, name: event.target.value }
              })
            }}
            disabled={!activeSequence}
          />

          <div className="component--editor-panel__header__ctas">
            <div className="component--editor-panel__header__ctas__controls">
              <div className="component--editor-panel__header__ctas__controls__control" onClick={() => { dispatch({ type: GlobalActionTypeList.SetEditorTimelineZoom, payload: getNextZoom('out') }) }}>
                <ZoomOut className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='medium' />
              </div>
              <div className="component--editor-panel__header__ctas__controls__control" onClick={() => { dispatch({ type: GlobalActionTypeList.SetEditorTimelineZoom, payload: getNextZoom('in') }) }}>
                <ZoomIn className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='medium' />
              </div>
            </div>
            <div className="component--editor-panel__header__ctas__controls">
              <div
                className={`component--editor-panel__header__ctas__controls__control ${hasSelection ? '' : 'disabled'}`}
                onClick={handleCopySelection}
                title="Copy"
              >
                <FileCopy className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='small' />
              </div>
              <div
                className={`component--editor-panel__header__ctas__controls__control ${copiedPattern?.notes.length ? '' : 'disabled'}`}
                onClick={handlePasteSelection}
                title="Paste"
              >
                <AssignmentReturned className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='small' />
              </div>
              <div
                className={`component--editor-panel__header__ctas__controls__control ${hasSelection ? '' : 'disabled'}`}
                onClick={handleDeleteSelection}
                title="Delete"
              >
                <Delete className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='small' />
              </div>
            </div>
            <div className="component--editor-panel__header__ctas__controls">
              <div
                className={`component--editor-panel__header__ctas__controls__control ${activeSequence ? '' : 'disabled'}`}
                onClick={handleExportTimeline}
                title="Export timeline"
              >
                <SaveAlt className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='small' />
              </div>
              <div
                className={`component--editor-panel__header__ctas__controls__control ${activeSequence ? '' : 'disabled'}`}
                onClick={handleImportTimeline}
                title="Import timeline"
              >
                <Publish className="component--editor-panel__header__ctas__controls__control__icon" color="primary" fontSize='small' />
              </div>
              <input
                ref={importInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={handleImportFileChange}
              />
            </div>
            <div className="component--editor-panel__header__ctas__controls">
              <div
                className={`component--editor-panel__header__ctas__controls__control ${isMuted ? 'active' : ''} ${state.control.isPlaying || !activeSequence ? 'disabled' : ''}`}
                onClick={handleMuteToggle}
              >
                <h5 className="component--editor-panel__header__ctas__controls__control__label">M</h5>
              </div>
              <div className="component--editor-panel__header__ctas__controls__control">
                <h5 className="component--editor-panel__header__ctas__controls__control__label">S</h5>
              </div>
            </div>
            <div className="component--editor-panel__header__ctas__controls">
              <div
                className={`component--editor-panel__header__ctas__controls__control ${state.editor.quantization.isActive ? 'active' : ''}`}
                onClick={() => {
                  dispatch({ type: GlobalActionTypeList.SetEditorQuantizationIsActive, payload: !state.editor.quantization.isActive })
                }}
              >
                <h5 className="component--editor-panel__header__ctas__controls__control__label">Q</h5>
              </div>
            </div>
            <div className="component--editor-panel__header__ctas__quantization">
              <div className="component--editor-panel__header__ctas__quantization__input">
                <DynamicWidthNumberInput
                  value={state.editor.quantization.numerator}
                  onChange={(num) => {
                    dispatch({ type: GlobalActionTypeList.SetEditorQuantizationNumerator, payload: num })
                  }}
                />
                <p className="component--editor-panel__header__ctas__quantization__input__slash">/</p>
                <DynamicWidthNumberInput
                  value={state.editor.quantization.denominator}
                  onChange={(num) => {
                    dispatch({ type: GlobalActionTypeList.SetEditorQuantizationDenominator, payload: num })
                  }}
                />
              </div>
              <p className="component--editor-panel__header__ctas__quantization__label">Quantization</p>
            </div>
            <div className="component--editor-panel__header__ctas__close" onClick={() => { dispatch({ type: GlobalActionTypeList.SetEditorTargetId, payload: '' }) }}>
              <CloseIcon className="component--editor-panel__header__ctas__close__icon" color="primary" fontSize='large' />
            </div>
          </div>
        </div>

        {/* Editor Panel Timeline (Body) */}
        <div className="component--editor-panel__timeline"
          ref={timelineRef}
          onMouseDown={handleTimelineMouseDown}
          onWheel={handleTimelineWheel}
          onScroll={(e) => {
            const isScrolledToRight = (e.target as HTMLDivElement).scrollLeft + (e.target as HTMLDivElement).clientWidth >= (e.target as HTMLDivElement).scrollWidth - 20
            if (isScrolledToRight) {
              setTempColumnCount(tempColumnCount + 10)
            }
          }}
        >
          <div className="component--editor-panel__timeline__playhead-track"
            style={{
              height: `${PLAYHEAD_TRACK_HEIGHT_PX}px`,
              minWidth: `${tempColumnCount * 100 * state.editor.timeline.zoom}px`
            }}
            onMouseDown={handlePlayheadDragStart}
          ></div>
          <div
            ref={pianoRollRef}
            onMouseDown={handleSelectionStart}
            onClick={handlePianoRollClick}
            onMouseMove={handlePianoRollMouseMove}
            onMouseUp={(e) => {
              handlePlayheadDragEnd()
              handlePianoRollDragEnd(e)
              handleSelectionEnd()
            }}
            onMouseLeave={(e) => {
              // setIsEraserHover(false)
              // setEraserPosition(null)
              // setEraserSource(null)
              setNoteEraserTargetId(null)
              handlePlayheadDragEnd()
              handlePianoRollDragEnd(e)
              handleSelectionEnd()
            }}
            className={`component--editor-panel__timeline__piano-roll`}
            style={{
              gridTemplateColumns: `repeat(${tempColumnCount}, ${100 * state.editor.timeline.zoom}px)`,
              gridTemplateRows: `repeat(${ROW_COUNT}, 1fr)`,
              width: `${tempColumnCount * 100 * state.editor.timeline.zoom}px`
            }}>
            {Array.from({ length: tempColumnCount * ROW_COUNT }).map((_, index) => (
              <div  
                key={index}
                className={`component--editor-panel__timeline__piano-roll__cell ${index < tempColumnCount ? 'top-border': ''} ${state.control.repeat?.isActive && index < tempColumnCount && (index >= (state.control.repeat.from.bar - 1) * state.control.signature.numerator + state.control.repeat.from.beat - 1 && index < (state.control.repeat.to.bar - 1) * state.control.signature.numerator + state.control.repeat.to.beat - 1) ? 'in-repeat': ''} ${Math.floor(index / tempColumnCount) === ROW_COUNT / 2 ? 'middle-border': ''} ${Math.floor(index / tempColumnCount) === ROW_COUNT - 1 ? 'bottom-border': ''} ${(index % tempColumnCount) % state.control.signature.numerator === 0 ? 'major-border': ''}`}
              >
                {((index % tempColumnCount) % state.control.signature.numerator === 0 && index < tempColumnCount) &&
                  <h6 className={`component--editor-panel__timeline__piano-roll__cell__major-border__label`}>{(index /  state.control.signature.numerator) + 1}</h6>
                }
              </div>
            ))}
            {hasSelection && cellSize.width > 0 && (
              <div
                className="component--editor-panel__timeline__piano-roll__selection"
                style={{
                  left: `${Math.min(selectionRange!.startBeat, selectionRange!.endBeat) * cellSize.width}px`,
                  width: `${Math.abs(selectionRange!.endBeat - selectionRange!.startBeat) * cellSize.width}px`
                }}
              ></div>
            )}
            <TimeIndicator
              state={state}
              cellSize={cellSize}
            />
            <div className="component--editor-panel__timeline__note-container">
              {state.sequences.find(s => s.id === state.editor.targetId)?.notes.sort((a, b) => a.position - b.position).map((sequence, index) => (
                <TimelineNote
                  key={`${sequence.id}-${sequence.position}`}
                  onMouseDown={(e) => handlePianoRollDragStart(e, sequence.id)}
                  onMouseEnter={(e) => handleNoteMouseEnter(e, sequence.id)}
                  onMouseMove={(e) => handleNoteMouseMove(e, sequence.id)}
                  onMouseLeave={() => handleNoteMouseLeave(sequence.id)}
                  isEraserHover={noteEraserTargetId === sequence.id}
                  state={state}
                  id={sequence.id}
                  position={sequence.position}
                  intensity={sequence.intensity}
                  cellSize={cellSize}
                  sequence={state.sequences.find(s => s.id === state.editor.targetId)?.notes || []}
                />
              ))}
            </div>      
          </div>
        </div>
      </div>
      <Dialog open={!!pendingImportPayload} onOpenChange={(open) => { if (!open) { setPendingImportPayload(null); setRememberImportChoice(false); } }}>
        {pendingImportPayload && (
          <DialogContent
            heading="Import timeline"
            description={`Imported timeline uses BPM ${pendingImportPayload.control.bpm} and signature ${pendingImportPayload.control.signature.numerator}/${pendingImportPayload.control.signature.denominator}. Choose how you want to apply it:`}
          >
            <Checkbox
              label="Remember my choice"
              checked={rememberImportChoice}
              onCheckedChange={(checked) => setRememberImportChoice(checked === true)}
            />
            <DialogActions>
              <Button
                variant="outline"
                onClick={() => {
                  dispatch({ type: GlobalActionTypeList.SetControlBpm, payload: pendingImportPayload.control.bpm })
                  dispatch({ type: GlobalActionTypeList.SetControlSignatureNumerator, payload: pendingImportPayload.control.signature.numerator })
                  dispatch({ type: GlobalActionTypeList.SetControlSignatureDenominator, payload: pendingImportPayload.control.signature.denominator })
                  finalizeImportChoice(pendingImportPayload, true, rememberImportChoice)
                }}
              >
                Maintain beats
              </Button>
              <Button
                variant="outline"
                onClick={() => finalizeImportChoice(pendingImportPayload, false, rememberImportChoice)}
              >
                Maintain timestamps
              </Button>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </div>
  )
}

export default EditorPanel;
